// src/pages/ChangelogPage.tsx — global mutation feed across all clients,
// built from the same version records that power per-client history.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { ChangelogResponse, VersionRecord } from '@eotir/oidcbridge-shared/api-contracts';

export function ChangelogPage() {
  const [clientFilter, setClientFilter] = useState('');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['changelog', clientFilter],
    queryFn: ({ signal }) =>
      api<ChangelogResponse>(
        `/api/changelog${clientFilter ? `?clientId=${encodeURIComponent(clientFilter)}` : ''}`,
        { signal },
      ),
  });

  return (
    <div>
      <div className="eo-eyebrow">Audit · Policy Versions</div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="eo-h1">Changelog</h1>
        <button type="button" onClick={() => refetch()} className="eo-btn eo-btn--ghost" disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value.trim())}
          placeholder="Filter by client id…"
          className="eo-input w-72 font-mono"
        />
      </div>

      {isLoading && <p className="text-sm text-ink-3">Loading…</p>}
      {error && <p className="text-sm text-err">Failed to load: {(error as Error).message}</p>}

      {data && data.entries.length === 0 && (
        <p className="text-sm text-ink-3">
          No recorded changes{clientFilter ? ` for '${clientFilter}'` : ''} — history starts
          with the first mutation after the versioning upgrade.
        </p>
      )}

      {data && data.entries.length > 0 && (
        <>
          {data.truncated && (
            <p className="eo-hint mb-2">Showing the most recent entries — older history exists.</p>
          )}
          <table className="eo-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Client</th>
                <th>Action</th>
                <th>Operator</th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => <ChangelogRow key={`${e.clientId}:${e.seq}`} e={e} />)}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function ChangelogRow({ e }: { e: VersionRecord }) {
  const [open, setOpen] = useState(false);
  const who = e.actorName ?? e.actorEmail ?? `member #${e.actor}`;
  const chip =
    e.action === 'purge' || e.action === 'delete'
      ? 'eo-chip--err'
      : e.action === 'rotate'
        ? 'eo-chip--warn'
        : e.action === 'create' || e.action === 'restore'
          ? 'eo-chip--ok'
          : 'eo-chip--ops';

  return (
    <tr>
      <td className="text-ink-3 whitespace-nowrap">{new Date(e.at).toLocaleString()}</td>
      <td>
        <Link to={`/clients/${e.clientId}`} className="eo-mono text-crimson-hover no-underline hover:text-white">
          {e.clientId}
        </Link>
        <span className="text-ink-4 ml-1.5 font-mono text-[10px]">v{e.seq}</span>
      </td>
      <td><span className={`eo-chip ${chip}`}>{e.action}</span></td>
      <td className="text-ink-2">{who}</td>
      <td>
        {e.diff.length === 0 ? (
          <span className="text-ink-4 text-[12px]">{e.note ?? '—'}</span>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="font-mono text-[10.5px] tracking-label uppercase text-crimson-hover hover:text-white"
          >
            {open ? '− hide' : `${e.diff.length} field${e.diff.length === 1 ? '' : 's'}`}
          </button>
        )}
        {open && (
          <div className="mt-1.5 space-y-1">
            {e.note && <div className="text-[11.5px] text-ink-3">{e.note}</div>}
            {e.diff.map((d) => (
              <div key={d.field} className="font-mono text-[11px] text-ink-2">
                <span className="text-ink">{d.field}</span>:{' '}
                <span className="text-err-soft break-all">{JSON.stringify(d.from)}</span>
                {' → '}
                <span className="text-ok-soft break-all">{JSON.stringify(d.to)}</span>
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}
